/**
 * Voice Activity Detection (VAD)
 * 
 * Based on Unity WF-SoftSkills proven techniques.
 * Distinguishes voice from ambient noise using spectral analysis.
 * 
 * Features:
 * - Adaptive noise floor (EMA-based, adapts to ambient environment)
 * - RMS (Root Mean Square) energy calculation
 * - Speech band ratio analysis (300-3400 Hz)
 * - Gate logic with hysteresis
 * - Pre-roll buffer for smooth transitions
 */

import { VAD_ACTIVITY_LOOKBACK_MS, VAD_CONFIG } from '../../config/realtime';
import { isMobile } from '../../utils/platformDetection';
import { logger } from '../../utils/logger';

// On mobile (especially Firefox Android) the browser's AEC is weaker and tends
// to leak the avatar's own voice into the mic. Raise the energy floor required
// to count a chunk as voiced *during avatar playback*, so the residue doesn't
// pass the gate. The non-playback path is unchanged so user speech in silence
// still passes normally.
//
// 2026-05: Halved from 0.060 → 0.030 (one binary-search step). Previous value
// was rejecting normal interruptions; AEC residue typically sits below 0.030
// so this still keeps the self-trigger guard.
// Next step if self-trigger comes back: 0.045 (midpoint between current and previous).
const PLAYBACK_ENERGY_MINIMUM_MOBILE = 0.030;

// Relaxed thresholds for atypical voices (e.g. esophageal/tracheoesophageal speech
// after laryngectomy). These voices have weak harmonics and broadband noise, so
// the standard 300–3400 Hz energy concentration check rejects them as non-voice.
// Energy floors (absoluteEnergyMinimum / playbackEnergyMinimum) are unchanged —
// real speech still produces enough RMS to clear them, and we keep echo rejection.
const ATYPICAL_VOICE_SPEECH_BAND_RATIO = 0.15;
const ATYPICAL_VOICE_NOISE_FLOOR_MARGIN = 1.2;
const ATYPICAL_VOICE_FAST_PATH_RATIO = 0.30;

interface VADChunk {
  timestamp: number;
  rms: number;
  ratio: number;
}

interface AudioActivitySample {
  timestamp: number;
  rms: number;
  isVoiced: boolean;
}

export interface VADState {
  gateOpen: boolean;
  voicedMs: number;
  silenceMs: number;
  preRollChunks: number;
  noiseFloor: number;
}

export class VoiceActivityDetection {
  private audioContext: AudioContext;
  private vadAnalyserNode: AnalyserNode | null = null;
  
  // VAD state
  private vadGateOpen = false;
  private vadAccumulatedVoicedMs = 0;
  private vadSilenceDurationMs = 0;
  private vadPreRollBuffer: VADChunk[] = [];
  private vadLastProcessTime = 0;
  private vadMonitorInterval: NodeJS.Timeout | null = null;
  
  // 🔧 Grace period: Allow brief silence gaps between syllables without resetting counter
  // This prevents the counter from resetting on micro-pauses between words/syllables
  private vadSilenceGraceMs = 0;
  private readonly SILENCE_GRACE_TOLERANCE_MS = 80; // Allow up to 80ms of silence
  
  // 🛡️ Audio activity history for speech_stopped validation
  private audioActivityHistory: AudioActivitySample[] = [];
  private readonly ACTIVITY_HISTORY_MS = 1000; // Keep last 1 second
  
  // Adaptive noise floor — tracks ambient RMS via EMA, persists across conversation turns
  private noiseFloor: number = VAD_CONFIG.noiseFloorInitial;
  
  // Polled every 20ms to check if avatar is currently playing audio.
  // When true, VAD raises thresholds to reject echo residue from AEC.
  private playbackActive = false;
  private isPlaybackActiveFn: (() => boolean) | null = null;

  // When enabled, relaxes the speech-band ratio and noise-floor margin so the
  // VAD recognises atypical voices (e.g. post-laryngectomy esophageal speech).
  private atypicalVoiceMode = false;
  
  // Early visual signal: fires once per speech segment when speech looks likely,
  // well before the gate opens. Drives waveform display without affecting audio streaming.
  private speechLikelyFired = false;
  private onSpeechLikelyCallback: (() => void) | null = null;

  // Recording state (external)
  private isRecording = false;

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
  }

  /**
   * Initialize VAD with analyser node
   */
  initialize(_sourceNode: MediaStreamAudioSourceNode, gainNode: GainNode): void {
    // Create dedicated analyser for VAD with larger FFT
    this.vadAnalyserNode = this.audioContext.createAnalyser();
    this.vadAnalyserNode.fftSize = VAD_CONFIG.fftSize;
    this.vadAnalyserNode.smoothingTimeConstant = VAD_CONFIG.smoothingTimeConstant;
    this.vadAnalyserNode.minDecibels = -90;
    this.vadAnalyserNode.maxDecibels = -10;
    
    // Connect to audio pipeline
    gainNode.connect(this.vadAnalyserNode);
    
    logger.debug('[🎯 VAD] Initialized with adaptive noise floor config:', {
      fftSize: VAD_CONFIG.fftSize,
      noiseFloorInitial: VAD_CONFIG.noiseFloorInitial,
      noiseFloorMargin: VAD_CONFIG.noiseFloorMarginMultiplier,
      absoluteEnergyMinimum: VAD_CONFIG.absoluteEnergyMinimum,
      speechBandRatio: VAD_CONFIG.speechBandRatioThreshold
    });
  }

  /**
   * Start VAD monitoring
   */
  startMonitoring(): void {
    if (this.vadMonitorInterval || !this.vadAnalyserNode) {
      return;
    }

    this.isRecording = true;
    this.vadLastProcessTime = Date.now();
    
    logger.debug('[🎯 VAD] 🚀 Starting Voice Activity Detection monitoring');
    
    // Check audio every 20ms (50 Hz)
    this.vadMonitorInterval = setInterval(() => {
      if (!this.vadAnalyserNode || !this.isRecording) {
        return;
      }

      // Determine whether avatar audio is currently playing.
      // When a polling function is registered, it is the source of truth — it checks
      // the real-time buffer/node state and naturally returns false once audio finishes.
      // The explicit setPlaybackActive flag serves as immediate override for stop events
      // (resetStreamingBuffer, stopAllPlayback) that need to take effect within the
      // same 20ms tick, before the polling function can reflect the change.
      if (this.isPlaybackActiveFn) {
        this.playbackActive = this.isPlaybackActiveFn();
      }

      const now = Date.now();
      const deltaMs = now - this.vadLastProcessTime;
      this.vadLastProcessTime = now;

      // Get time domain data for RMS
      const timeDataLength = this.vadAnalyserNode.fftSize;
      const timeData = new Uint8Array(timeDataLength);
      this.vadAnalyserNode.getByteTimeDomainData(timeData);

      // Get frequency data for speech band ratio
      const frequencyDataLength = this.vadAnalyserNode.frequencyBinCount;
      const frequencyData = new Uint8Array(frequencyDataLength);
      this.vadAnalyserNode.getByteFrequencyData(frequencyData);

      // Calculate metrics
      const rms = this.calculateRMS(timeData);
      const speechBandRatio = this.calculateSpeechBandRatio(frequencyData);

      // 🛡️ Track audio activity for speech_stopped validation
      const isVoiced = this.isVoicedChunk(rms, speechBandRatio);
      this.trackAudioActivity(now, rms, isVoiced);

      // Adapt noise floor when gate is closed, chunk is not voiced, and avatar is not playing.
      // During playback, echo residue would contaminate the floor.
      if (!this.vadGateOpen && !isVoiced && !this.playbackActive) {
        const alpha = VAD_CONFIG.noiseFloorAdaptationRate;
        const raw = this.noiseFloor * (1 - alpha) + rms * alpha;
        this.noiseFloor = Math.max(VAD_CONFIG.noiseFloorMinimum, Math.min(VAD_CONFIG.noiseFloorMaximum, raw));
      }

      // Update pre-roll buffer
      this.updatePreRollBuffer(rms, speechBandRatio);

      // Process gate logic
      this.processVADGate(rms, speechBandRatio, deltaMs);

    }, 20);
  }

  /**
   * Stop VAD monitoring
   */
  stopMonitoring(): void {
    if (this.vadMonitorInterval) {
      clearInterval(this.vadMonitorInterval);
      this.vadMonitorInterval = null;
      logger.debug('[🎯 VAD] 🛑 VAD monitoring stopped');
    }
    
    this.isRecording = false;
    this.resetState();
  }

  /**
   * Get current VAD state
   */
  getState(): VADState {
    return {
      gateOpen: this.vadGateOpen,
      voicedMs: this.vadAccumulatedVoicedMs,
      silenceMs: this.vadSilenceDurationMs,
      preRollChunks: this.vadPreRollBuffer.length,
      noiseFloor: this.noiseFloor,
    };
  }

  /**
   * Calculate RMS (Root Mean Square) energy from time domain data
   */
  private calculateRMS(timeData: Uint8Array): number {
    let sum = 0;
    for (let i = 0; i < timeData.length; i++) {
      // Time domain data is centered at 128 (silence = 128)
      const normalized = (timeData[i] - 128) / 128.0;
      sum += normalized * normalized;
    }
    const rms = Math.sqrt(sum / timeData.length);
    return rms;
  }

  /**
   * Calculate Speech Band Ratio
   * Proportion of energy in human voice frequencies (300-3400 Hz)
   */
  private calculateSpeechBandRatio(frequencyData: Uint8Array): number {
    if (!this.vadAnalyserNode) {
      return 0;
    }

    const sampleRate = this.audioContext.sampleRate;
    const fftSize = this.vadAnalyserNode.fftSize;
    const binWidth = sampleRate / fftSize;
    
    // Calculate frequency bins for 300-3400 Hz
    const startBin = Math.floor(VAD_CONFIG.speechBandLowFreq / binWidth);
    const endBin = Math.floor(VAD_CONFIG.speechBandHighFreq / binWidth);
    
    let totalEnergy = 0;
    let speechEnergy = 0;
    
    for (let i = 0; i < frequencyData.length; i++) {
      const energy = frequencyData[i] * frequencyData[i];
      totalEnergy += energy;
      
      if (i >= startBin && i <= endBin) {
        speechEnergy += energy;
      }
    }
    
    if (totalEnergy < 1e-12) {
      return 0;
    }
    
    return speechEnergy / totalEnergy;
  }

  /**
   * Check if current audio chunk is "voiced" (contains speech).
   * Uses adaptive noise floor instead of a static RMS threshold.
   */
  private isVoicedChunk(rms: number, speechBandRatio: number): boolean {
    const energyFloor = this.playbackActive
      ? (isMobile() ? PLAYBACK_ENERGY_MINIMUM_MOBILE : VAD_CONFIG.playbackEnergyMinimum)
      : VAD_CONFIG.absoluteEnergyMinimum;

    if (rms < energyFloor) {
      return false;
    }

    const baseMargin = this.atypicalVoiceMode
      ? ATYPICAL_VOICE_NOISE_FLOOR_MARGIN
      : VAD_CONFIG.noiseFloorMarginMultiplier;
    const margin = this.playbackActive
      ? baseMargin * VAD_CONFIG.playbackMarginBoost
      : baseMargin;

    const adaptiveThreshold = this.noiseFloor * margin;

    const ratioThreshold = this.atypicalVoiceMode
      ? ATYPICAL_VOICE_SPEECH_BAND_RATIO
      : VAD_CONFIG.speechBandRatioThreshold;
    const fastPathRatio = this.atypicalVoiceMode
      ? ATYPICAL_VOICE_FAST_PATH_RATIO
      : VAD_CONFIG.fastPathRatioThreshold;

    if (!this.playbackActive) {
      if (rms >= VAD_CONFIG.fastPathRmsThreshold && speechBandRatio >= fastPathRatio) {
        return true;
      }
    }

    return rms >= adaptiveThreshold && speechBandRatio >= ratioThreshold;
  }

  /**
   * Process VAD gate logic
   * 
   * ⚠️⚠️⚠️ CRITICAL CRITICAL CRITICAL ⚠️⚠️⚠️
   * 
   * CLIENT-SIDE VAD ONLY OPENS THE GATE - NEVER CLOSES IT!
   * 
   * THE GATE IS ONLY CLOSED BY:
   * 1. OpenAI semantic_vad (speech_stopped event) - THE ONLY CORRECT WAY
   * 2. Manual reset via resetVADState() when conversation turn ends
   * 
   * DO NOT ADD LOGIC TO CLOSE THE GATE BASED ON SILENCE!
   * DO NOT ADD LOGIC TO CLOSE THE GATE BASED ON TIMEOUTS!
   * DO NOT ADD ANY CLIENT-SIDE LOGIC TO CLOSE THE GATE!
   * 
   * WHY? Because only OpenAI's semantic_vad understands conversational context
   * and can correctly detect when the user has FINISHED speaking (not just pausing).
   * 
   * If you add client-side gate closing logic, you WILL create:
   * - Empty transcript bubbles
   * - Split transcripts (user speaks, pauses, continues → 2 bubbles instead of 1)
   * - Race conditions between client and server VAD
   * 
   * ⚠️⚠️⚠️ DO NOT MODIFY THIS UNLESS YOU KNOW WHAT YOU'RE DOING ⚠️⚠️⚠️
   */
  private processVADGate(rms: number, speechBandRatio: number, deltaMs: number): void {
    const isVoiced = this.isVoicedChunk(rms, speechBandRatio);
    
    if (this.vadGateOpen) {
      // ============================================================================
      // GATE IS OPEN - IT STAYS OPEN!
      // ============================================================================
      // Once the gate opens, it NEVER closes automatically.
      // Only OpenAI semantic_vad (via resetVADState) can close it.
      // 
      // This ensures continuous audio streaming to OpenAI so semantic_vad can:
      // - Detect natural pauses vs. end of speech
      // - Understand conversational context
      // - Make intelligent decisions about when user finished speaking
      // ============================================================================
      
      // Nothing to do here - gate stays open until OpenAI says user stopped speaking
      
    } else {
      // Gate is closed - check if we should open it
      if (isVoiced) {
        // Voiced frame - accumulate and reset grace period
        this.vadAccumulatedVoicedMs += deltaMs;
        this.vadSilenceGraceMs = 0; // Reset grace period on voiced frame

        // Early visual signal: fire onSpeechLikely before the gate opens.
        // During playback we skip this — the stricter playback thresholds handle
        // interruption separately, and firing early would cause false waveforms
        // from echo residue.
        if (!this.speechLikelyFired && !this.playbackActive) {
          const isFastPath = rms >= VAD_CONFIG.fastPathRmsThreshold
            && speechBandRatio >= VAD_CONFIG.fastPathRatioThreshold;

          if (isFastPath || this.vadAccumulatedVoicedMs >= VAD_CONFIG.speechLikelyThresholdMs) {
            this.speechLikelyFired = true;
            this.onSpeechLikelyCallback?.();
            logger.debug('[🎯 VAD] 👁️ Speech likely signal fired at',
              this.vadAccumulatedVoicedMs.toFixed(0), 'ms (fastPath:', isFastPath, ')');
          }
        }
        
        const requiredVoicedMs = this.playbackActive
          ? VAD_CONFIG.playbackMinVoicedTimeMs
          : VAD_CONFIG.minVoicedTimeMs;

        if (this.vadAccumulatedVoicedMs >= requiredVoicedMs) {
          this.vadGateOpen = true;
          this.vadSilenceDurationMs = 0;
          logger.debug('[🎯 VAD] 🚪 Gate OPENED after', this.vadAccumulatedVoicedMs.toFixed(0), 'ms voiced audio',
            '(playback:', this.playbackActive, ', noiseFloor:', this.noiseFloor.toFixed(4), ')');
        }
      } else {
        if (this.vadAccumulatedVoicedMs > 0) {
          // Allow brief silence gaps between syllables/words.
          // During playback: shorter tolerance (AEC causes micro-gaps in real speech,
          // but echo bursts have longer gaps). Normal mode: standard tolerance.
          const graceTolerance = this.playbackActive
            ? this.SILENCE_GRACE_TOLERANCE_MS / 2  // 40ms during playback (tighter but not zero)
            : this.SILENCE_GRACE_TOLERANCE_MS;      // 80ms normal
          
          this.vadSilenceGraceMs += deltaMs;
          if (this.vadSilenceGraceMs >= graceTolerance) {
            this.vadAccumulatedVoicedMs = 0;
            this.vadSilenceGraceMs = 0;
          }
        }
      }
    }
  }

  /**
   * Update pre-roll buffer with current audio metrics
   * Keeps last 700ms of audio data
   */
  private updatePreRollBuffer(rms: number, speechBandRatio: number): void {
    const now = Date.now();
    
    this.vadPreRollBuffer.push({
      timestamp: now,
      rms,
      ratio: speechBandRatio
    });
    
    // Trim buffer to keep only last 700ms
    const cutoffTime = now - VAD_CONFIG.preRollBufferMs;
    this.vadPreRollBuffer = this.vadPreRollBuffer.filter(chunk => chunk.timestamp >= cutoffTime);
  }

  /**
   * 🛡️ Track audio activity for validation
   */
  private trackAudioActivity(timestamp: number, rms: number, isVoiced: boolean): void {
    // Add new sample
    this.audioActivityHistory.push({ timestamp, rms, isVoiced });

    // Remove samples older than ACTIVITY_HISTORY_MS
    const cutoffTime = timestamp - this.ACTIVITY_HISTORY_MS;
    this.audioActivityHistory = this.audioActivityHistory.filter(
      sample => sample.timestamp >= cutoffTime
    );
  }

  /**
   * 🛡️ Check if there was recent audio activity (for speech_stopped validation)
   * Returns true if user was speaking in the last 500ms
   */
  public hasRecentAudioActivity(lookbackMs: number = VAD_ACTIVITY_LOOKBACK_MS): boolean {
    if (this.audioActivityHistory.length === 0) {
      return false;
    }

    // Use Date.now() to match timestamps stored in audioActivityHistory
    const now = Date.now();
    const cutoffTime = now - lookbackMs;
    
    // Get samples from lookback period
    const recentSamples = this.audioActivityHistory.filter(
      sample => sample.timestamp >= cutoffTime
    );

    if (recentSamples.length === 0) {
      return false;
    }

    // Calculate percentage of voiced samples
    const voicedCount = recentSamples.filter(s => s.isVoiced).length;
    const voicedPercentage = voicedCount / recentSamples.length;

    // If 30%+ of recent samples were voiced, user was likely still speaking
    return voicedPercentage >= 0.30;
  }

  /**
   * Reset VAD state (public - called when conversation turn ends).
   * Noise floor is intentionally preserved — it represents the environment, not conversation state.
   */
  resetState(): void {
    this.vadGateOpen = false;
    this.vadAccumulatedVoicedMs = 0;
    this.vadSilenceDurationMs = 0;
    this.vadSilenceGraceMs = 0;
    this.vadPreRollBuffer = [];
    this.audioActivityHistory = [];
    this.speechLikelyFired = false;
    logger.debug('[🎯 VAD] Gate state reset - ready for next turn (noiseFloor preserved:', this.noiseFloor.toFixed(4), ')');
  }

  /**
   * Register a callback for the early "speech likely" visual signal.
   * Fires much sooner than gate open (~60ms of voiced frames, or immediately
   * on a fast-path frame). Used purely for waveform display — does NOT
   * affect audio streaming to OpenAI.
   */
  setOnSpeechLikely(fn: () => void): void {
    this.onSpeechLikelyCallback = fn;
  }

  /**
   * Register a callback that the VAD polls every 20ms to check playback state.
   * This is more reliable than event-based setPlaybackActive because it tracks
   * the actual real-time state of the audio buffer, not events that may fire
   * too early (finishStreamingResponse) or too late.
   */
  setPlaybackChecker(fn: () => boolean): void {
    this.isPlaybackActiveFn = fn;
  }

  /**
   * Signal whether avatar audio is currently playing.
   * Kept for explicit stop signals (stopAllPlayback, resetStreamingBuffer).
   */
  setPlaybackActive(active: boolean): void {
    this.playbackActive = active;
  }

  /**
   * Toggle relaxed thresholds for atypical voices (e.g. post-laryngectomy
   * esophageal/tracheoesophageal speech). When on, lowers the speech-band ratio
   * and noise-floor margin so voices with weak harmonics still open the gate.
   * Trade-off: more false positives in noisy environments.
   */
  setAtypicalVoiceMode(enabled: boolean): void {
    if (this.atypicalVoiceMode === enabled) {
      return;
    }
    this.atypicalVoiceMode = enabled;
    logger.debug('[🎯 VAD] Atypical voice mode:', enabled ? 'ENABLED' : 'disabled');
  }

  /**
   * Get current adaptive noise floor value (for debugging/logging)
   */
  getNoiseFloor(): number {
    return this.noiseFloor;
  }

  /**
   * Clean up VAD resources
   */
  cleanup(): void {
    this.stopMonitoring();
    
    if (this.vadAnalyserNode) {
      this.vadAnalyserNode.disconnect();
      this.vadAnalyserNode = null;
    }
  }
}

